/**
 * Native cart experiment, recommendation actions and first-party measurement.
 * Warehouse remains responsible for cart section rendering and quantity changes.
 */

(function () {
  'use strict';

  var experiment = window.ffCartExperiment || {
    id: 'native-cart-v1',
    enabled: false,
    configured: 'page',
    experience: 'page',
    drawerAllocation: 0,
    debug: false
  };
  var viewedUpsells = {};
  var selectedUpsellVariants = {};

  function upsellSelectionKey(card) {
    if (!card) return '';
    return [
      card.getAttribute('data-source-product-id') || '',
      card.getAttribute('data-product-id') || ''
    ].join(':');
  }

  function route(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  function jsonRoute(name, fallback) {
    return route(name, fallback).replace(/\/$/, '') + '.js';
  }

  function publish(eventName, data) {
    var payload = Object.assign({
      experiment_id: experiment.id,
      cart_experience: experiment.experience,
      experiment_configured_as: experiment.configured,
      debug_assignment: experiment.debug
    }, data || {});

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ff_cart: payload });

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (error) {}
  }

  function publishAssignment() {
    var key = 'ff_cart_assignment_published_' + experiment.id;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (error) {}

    publish('ff_cart_experiment_assigned', {
      drawer_allocation: experiment.drawerAllocation
    });
  }

  function recordUpsellViews(scope) {
    var root = scope || document;
    var cards = root.querySelectorAll('[data-ff-upsell-card]');

    Array.prototype.forEach.call(cards, function (card) {
      var productId = card.getAttribute('data-product-id');
      var sourceProductId = card.getAttribute('data-source-product-id');
      var key = experiment.experience + ':' + productId + ':' + sourceProductId;
      if (viewedUpsells[key]) return;
      viewedUpsells[key] = true;

      publish('ff_cart_upsell_viewed', {
        product_id: productId,
        source_product_id: sourceProductId
      });
    });
  }

  function updateHeaderCartCount(itemCount) {
    var isEmpty = itemCount === 0;
    var countElements = document.querySelectorAll('.header__cart-count');

    Array.prototype.forEach.call(countElements, function (element) {
      element.textContent = itemCount;
      element.classList.toggle('header__cart-count--empty', isEmpty);
      element.classList.toggle('header__cart-count--has-items', !isEmpty);
      element.classList.toggle('ff-basket-count--empty', isEmpty);
    });

    if (window.theme) window.theme.cartCount = itemCount;
  }

  function refreshCartPage() {
    var currentCart = document.querySelector('[data-section-type="cart"]');
    if (!currentCart) return Promise.reject(new Error('Basket section is unavailable.'));

    var sectionId = currentCart.getAttribute('data-section-id');
    if (!sectionId) return Promise.reject(new Error('Basket section could not be refreshed.'));

    var sectionUrl = route('cartUrl', '/cart') + '?section_id=' + encodeURIComponent(sectionId);

    return Promise.all([
      fetch(sectionUrl, {
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Basket section could not be refreshed.');
        return response.text();
      }),
      fetch(jsonRoute('cartUrl', '/cart'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Basket count could not be refreshed.');
        return response.json();
      })
    ]).then(function (results) {
      var container = document.createElement('div');
      container.innerHTML = results[0];

      var refreshedCart = container.querySelector('[data-section-type="cart"]');
      if (!refreshedCart) throw new Error('Basket section returned no content.');

      currentCart.innerHTML = refreshedCart.innerHTML;
      updateHeaderCartCount(results[1].item_count);
      currentCart.dispatchEvent(new CustomEvent('cart:rerendered', { bubbles: true }));
    });
  }

  function updateDrawerQuantity(drawer, control) {
    if (control.getAttribute('aria-busy') === 'true') return;

    var line = parseInt(control.getAttribute('data-line'), 10);
    var quantity = parseInt(control.getAttribute('data-quantity'), 10);
    if (!line || Number.isNaN(quantity)) return;

    control.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch(jsonRoute('cartChangeUrl', '/cart/change'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to update basket');
        return response.json();
      })
      .then(function () {
        drawer.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { scrollToTop: false }
        }));
      })
      .catch(function () {
        control.removeAttribute('aria-busy');
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
  }

  /* AMP mounts its slide cart at document level, outside every header and
     navigation stacking context. Warehouse still keeps a reference to the
     same element after it is moved, so its rendering API continues to work
     while the drawer gets one stable viewport coordinate system. */
  function mountDrawerAtDocumentLevel(drawer) {
    if (drawer.closest('#ff-native-cart-portal')) return;

    var backdrop = drawer.nextElementSibling;
    var portal = document.getElementById('ff-native-cart-portal');

    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'ff-native-cart-portal';
      document.body.appendChild(portal);
    }

    portal.appendChild(drawer);
    if (backdrop && backdrop.classList.contains('ff-cart-drawer-backdrop')) {
      portal.appendChild(backdrop);
    }
  }

  function watchDrawer() {
    var drawer = document.getElementById('mini-cart');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    mountDrawerAtDocumentLevel(drawer);

    var wasOpen = drawer.getAttribute('aria-hidden') === 'false';
    var pressedDrawerTarget = null;
    var replayingDrawerClick = false;
    var pressedTargetResetTimer = null;

    /* Warehouse retargets a pointer sequence to the page beneath the fixed
       drawer before its click handler runs. Preserve the original drawer
       target and replay that one locally instead. */
    window.addEventListener('pointerdown', function (event) {
      if (replayingDrawerClick || drawer.getAttribute('aria-hidden') !== 'false') return;
      if (!event.target.closest || event.target.closest('#mini-cart') !== drawer) return;

      window.clearTimeout(pressedTargetResetTimer);
      pressedDrawerTarget = event.target.closest && event.target.closest('a, button, input, textarea, select, summary, label, [data-action]');
      if (!pressedDrawerTarget || !drawer.contains(pressedDrawerTarget)) pressedDrawerTarget = event.target;
      pressedTargetResetTimer = window.setTimeout(function () {
        pressedDrawerTarget = null;
      }, 900);
    }, true);

    window.addEventListener('pointercancel', function () {
      window.clearTimeout(pressedTargetResetTimer);
      pressedDrawerTarget = null;
    }, true);

    window.addEventListener('click', function (event) {
      if (replayingDrawerClick || !pressedDrawerTarget) return;

      var target = pressedDrawerTarget;
      window.clearTimeout(pressedTargetResetTimer);
      pressedDrawerTarget = null;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!target.isConnected) return;
      replayingDrawerClick = true;
      try {
        target.click();
      } finally {
        replayingDrawerClick = false;
      }
    }, true);

    /* Warehouse considers the drawer outside its cart click boundary. Keep all
       drawer clicks local so its global outside-click handler cannot close the
       drawer before an internal action completes. */
    drawer.addEventListener('click', function (event) {
      if (!event.target.closest) return;

      handleDocumentClick(event);

      var closeControl = event.target.closest('[data-action="toggle-mini-cart"]');
      if (closeControl && drawer.contains(closeControl)) {
        event.preventDefault();
        event.stopPropagation();
        var headerToggle = document.querySelector('[aria-controls="' + drawer.id + '"]');
        if (headerToggle) headerToggle.click();
        return;
      }

      var quantityControl = event.target.closest('[data-action="decrease-quantity"], [data-action="increase-quantity"]');
      if (quantityControl && drawer.contains(quantityControl)) {
        event.preventDefault();
        event.stopPropagation();
        updateDrawerQuantity(drawer, quantityControl);
        return;
      }

      event.stopPropagation();
    });

    var observer = new MutationObserver(function () {
      var isOpen = drawer.getAttribute('aria-hidden') === 'false';

      if (isOpen && !wasOpen) {
        publish('ff_cart_opened');
      }
      if (isOpen) {
        restoreUpsellSelections(drawer);
        recordUpsellViews(drawer);
      }
      wasOpen = isOpen;
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true
    });
  }

  function handleUpsellSubmit(event) {
    var form = event.target.closest && event.target.closest('[data-ff-upsell-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    var button = form.querySelector('[data-ff-upsell-add]');
    var errorElement = form.querySelector('[data-ff-upsell-error]');
    var card = form.closest('[data-ff-upsell-card]');
    var selectedVariant = card && card.querySelector('[data-ff-upsell-variant]');
    var selectionKey = upsellSelectionKey(card);
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    var formData = new FormData(form);
    if (selectedVariant) {
      formData.set('id', selectedUpsellVariants[selectionKey] || selectedVariant.value);
    }

    fetch(jsonRoute('cartAddUrl', '/cart/add'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.description || body.message || 'This item could not be added.');
          return body;
        });
      })
      .then(function (item) {
        if (selectionKey) delete selectedUpsellVariants[selectionKey];
        publish('ff_cart_upsell_added', {
          product_id: card && card.getAttribute('data-product-id'),
          source_product_id: card && card.getAttribute('data-source-product-id'),
          variant_id: item.variant_id || item.id,
          quantity: 1
        });
        syncExperimentAttributes();

        if (window.theme && window.theme.pageType === 'cart') {
          return refreshCartPage().catch(function () {
            window.location.reload();
          });
        }

        form.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { quantity: 1, button: button }
        }));
      })
      .catch(function (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (errorElement) {
          errorElement.textContent = error.message;
          errorElement.hidden = false;
        }
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      })
      .finally(function () {
        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }

        if (window.theme && window.theme.pageType === 'cart') {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      });
  }

  /* Warehouse also listens for product-form submissions. Intercept the
     recommendation button at click-capture time so the selected variant is
     committed before any generic product-form handler can read the form. */
  function handleUpsellAddClick(event) {
    var button = event.target.closest && event.target.closest('[data-ff-upsell-add]');
    if (!button) return;

    var form = button.closest('[data-ff-upsell-form]');
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    handleUpsellSubmit({
      target: form,
      preventDefault: function () {},
      stopImmediatePropagation: function () {}
    });
  }

  function updateUpsellVariantPresentation(select, rememberSelection) {
    var card = select.closest('[data-ff-upsell-card]');
    var option = select.options[select.selectedIndex];
    if (!card || !option) return;

    var price = card.querySelector('[data-ff-upsell-price]');
    var compareAt = card.querySelector('[data-ff-upsell-compare-at]');
    var compareAtValue = option.getAttribute('data-compare-at-price') || '';
    var selectionKey = upsellSelectionKey(card);

    if (rememberSelection && selectionKey) selectedUpsellVariants[selectionKey] = select.value;

    if (price) {
      price.textContent = option.getAttribute('data-price') || '';
      price.classList.toggle('ff-cart-upsell__sale-price', Boolean(compareAtValue));
    }

    if (compareAt) {
      compareAt.textContent = compareAtValue;
      compareAt.hidden = !compareAtValue;
    }
  }

  function restoreUpsellSelections(scope) {
    var root = scope || document;
    var selects = root.querySelectorAll('[data-ff-upsell-variant]');

    Array.prototype.forEach.call(selects, function (select) {
      var key = upsellSelectionKey(select.closest('[data-ff-upsell-card]'));
      var storedVariant = key && selectedUpsellVariants[key];
      if (!storedVariant || select.value === storedVariant) return;
      if (!Array.prototype.some.call(select.options, function (option) { return option.value === storedVariant; })) return;

      select.value = storedVariant;
      updateUpsellVariantPresentation(select, false);
    });
  }

  function handleUpsellVariantChange(event) {
    var select = event.target.closest && event.target.closest('[data-ff-upsell-variant]');
    if (!select) return;
    updateUpsellVariantPresentation(select, true);
  }

  function showAddFeedback(form, message, isError) {
    var feedback = form.querySelector('[data-ff-cart-add-feedback]');
    if (!feedback) {
      feedback = document.createElement('p');
      feedback.setAttribute('data-ff-cart-add-feedback', '');
      feedback.setAttribute('role', 'status');
      feedback.className = 'ff-cart-add-feedback';
      form.appendChild(feedback);
    }
    feedback.textContent = message;
    feedback.classList.toggle('ff-cart-add-feedback--error', Boolean(isError));
  }

  function handleCartAddSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || form.hasAttribute('data-ff-upsell-form')) return;
    if (experiment.experience !== 'drawer' || !document.getElementById('mini-cart')) return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var button = event.submitter || form.querySelector('[type="submit"]');
    var formData = new FormData(form);
    var quantity = parseInt(formData.get('quantity'), 10) || 1;

    if (button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch(jsonRoute('cartAddUrl', '/cart/add'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.description || body.message || 'This item could not be added.');
          return body;
        });
      })
      .then(function () {
        syncExperimentAttributes();
        form.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { quantity: quantity, button: button }
        }));
      })
      .catch(function (error) {
        showAddFeedback(form, error.message, true);
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      })
      .finally(function () {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
  }

  function handleNoteSave(button) {
    var note = document.querySelector('[data-ff-cart-note]');
    var status = document.querySelector('[data-ff-cart-note-status]');
    if (!note || button.disabled) return;

    button.disabled = true;
    if (status) status.textContent = 'Saving…';

    fetch(jsonRoute('cartUpdateUrl', '/cart/update'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ note: note.value })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to save note');
        if (status) status.textContent = 'Saved';
      })
      .catch(function () {
        if (status) status.textContent = 'Please try again';
      })
      .finally(function () {
        button.disabled = false;
      });
  }

  function syncExperimentAttributes() {
    return fetch(jsonRoute('cartUrl', '/cart'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (!cart.item_count) return null;
        if (
          cart.attributes &&
          cart.attributes.ff_cart_experience === experiment.experience &&
          cart.attributes.ff_cart_experiment === experiment.id
        ) return null;

        return fetch(jsonRoute('cartUpdateUrl', '/cart/update'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              ff_cart_experience: experiment.experience,
              ff_cart_experiment: experiment.id
            }
          })
        }).then(function (response) {
          if (!response.ok) throw new Error('Unable to attribute basket');
        });
      })
      .catch(function () {});
  }

  function handleDocumentClick(event) {
    var noteButton = event.target.closest && event.target.closest('[data-ff-cart-note-save]');
    if (noteButton) {
      handleNoteSave(noteButton);
      return;
    }

    var checkout = event.target.closest && event.target.closest('[data-ff-cart-checkout], [name="checkout"]');
    if (checkout) publish('ff_cart_checkout_clicked');
  }

  function handleProductAddedForPage() {
    if (experiment.experience !== 'page' || window.theme.pageType === 'cart') return;
    syncExperimentAttributes().finally(function () {
      window.location.assign(route('cartUrl', '/cart'));
    });
  }

  function init() {
    if (!experiment.enabled) return;
    publishAssignment();
    watchDrawer();
    recordUpsellViews(document.querySelector('[data-section-type="cart"]') || document);
    syncExperimentAttributes();

    document.addEventListener('submit', handleUpsellSubmit, true);
    document.addEventListener('submit', handleCartAddSubmit, true);
    document.addEventListener('click', handleUpsellAddClick, true);
    document.addEventListener('change', handleUpsellVariantChange);
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('product:added', handleProductAddedForPage);
    document.addEventListener('cart:rerendered', function () {
      if (window.theme.pageType === 'cart') {
        restoreUpsellSelections(document);
        recordUpsellViews(document);
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
